import { useState, useRef, useMemo, useEffect } from 'react';
import { useApp } from '../context/AppContext';
import { agreementService } from '../api/services';
import { CONTRACT_TOKEN_GROUPS } from '../data/contractTokens';
import { sampleContractContext } from '../utils/contractContext';
import { printContract, sanitizeHtml } from '../utils/renderContract';
import ContractDocument from '../components/ContractDocument';

const TEMPLATE_ICONS = ['📄', '🏗️', '📋', '📝', '🚗', '🚐', '🏍️', '⚙️', '📅'];

const APPLIES_TO = [
  { val: 'rental', label: '🚗 Short-term', desc: 'Used for daily/weekly rental contracts' },
  { val: 'lease',  label: '📅 Long-term lease', desc: 'Used for monthly leasing contracts' },
  { val: 'both',   label: '🔁 Both', desc: 'Used for any contract type' },
];

const BLANK_TEMPLATE = {
  id: '',
  name: '',
  icon: '📄',
  desc: '',
  appliesTo: 'both',
  body: '',
  clauses: [],   // legacy / fallback — not edited in the new UI
  isDefault: false,
};

// Convert legacy template (clauses-only) → body HTML so editing them in the new editor works
function legacyToBody(tmpl) {
  if (tmpl.body) return tmpl.body;
  if (!tmpl.clauses || tmpl.clauses.length === 0) return '';
  return '<h2>Terms &amp; Conditions</h2><ol>'
    + tmpl.clauses.map(c => `<li>${escapeHtml(c)}</li>`).join('')
    + '</ol>';
}

function escapeHtml(s) {
  return String(s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

export default function AgreementsPage() {
  const { agreementTemplates, setAgreementTemplates, resetAgreementTemplates, companySettings, showToast } = useApp();

  const [selectedId, setSelectedId] = useState(agreementTemplates[0]?.id || null);
  const [draft, setDraft] = useState({ ...BLANK_TEMPLATE });
  const [dirty, setDirty] = useState(false);
  const [creating, setCreating] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [openGroups, setOpenGroups] = useState({ lessee: true, vehicle: true, rental: true, lessor: false, signature: false });

  const editorRef = useRef(null);

  const selected = useMemo(
    () => creating ? draft : agreementTemplates.find(t => t.id === selectedId),
    [agreementTemplates, selectedId, creating, draft]
  );

  // Sync editor when selected template changes
  useEffect(() => {
    if (creating) return;
    const t = agreementTemplates.find(x => x.id === selectedId);
    if (t) {
      const initial = { ...t, body: legacyToBody(t), appliesTo: t.appliesTo || 'both' };
      setDraft(initial);
      setDirty(false);
      if (editorRef.current) editorRef.current.innerHTML = initial.body || '';
    }
  }, [selectedId, agreementTemplates, creating]);

  // Live preview context
  const previewCtx = useMemo(() => sampleContractContext(companySettings || {}), [companySettings]);

  // ── CRUD ─────────────────────────────────────────────────────────
  function startNew() {
    const blank = { ...BLANK_TEMPLATE, id: 'agr-' + Date.now() };
    setDraft(blank);
    setCreating(true);
    setSelectedId(null);
    setDirty(false);
    setTimeout(() => { if (editorRef.current) editorRef.current.innerHTML = ''; }, 0);
  }

  function cancelEdit() {
    setCreating(false);
    if (agreementTemplates[0]) setSelectedId(agreementTemplates[0].id);
    setDirty(false);
  }

  function save() {
    if (!draft.name.trim()) { showToast('Template name is required'); return; }
    const next = { ...draft, body_html: draft.body };
    if (creating) {
      if (next.isDefault) {
        setAgreementTemplates(prev => [...prev.map(t => ({ ...t, isDefault: false })), next]);
      } else {
        setAgreementTemplates(prev => [...prev, next]);
      }
      agreementService.create(next).then(created => {
        setAgreementTemplates(prev => prev.map(t => t.id === next.id ? created : t));
        setSelectedId(created.id);
      }).catch(() => {});
      showToast('✓ Template created');
      setCreating(false);
      setSelectedId(next.id);
    } else {
      if (next.isDefault) {
        setAgreementTemplates(prev => prev.map(t => t.id === next.id ? next : { ...t, isDefault: false }));
      } else {
        setAgreementTemplates(prev => prev.map(t => t.id === next.id ? next : t));
      }
      const tmpl = agreementTemplates.find(t => t.id === next.id);
      agreementService.update(tmpl?._backendId || next.id, next).catch(() => {});
      showToast('✓ Template saved');
    }
    setDirty(false);
  }

  function tryDelete(tmpl) {
    if (tmpl.isDefault) { showToast('⚠ Cannot delete the default template'); return; }
    setConfirmDelete(tmpl);
  }

  function doDelete() {
    if (!confirmDelete) return;
    setAgreementTemplates(prev => prev.filter(t => t.id !== confirmDelete.id));
    agreementService.delete(confirmDelete._backendId || confirmDelete.id).catch(() => {});
    showToast('✓ Template deleted');
    if (selectedId === confirmDelete.id) {
      setSelectedId(agreementTemplates.find(t => t.id !== confirmDelete.id)?.id || null);
    }
    setConfirmDelete(null);
  }

  function update(patch) {
    setDraft(d => ({ ...d, ...patch }));
    setDirty(true);
  }

  // ── Editor (contenteditable) ────────────────────────────────────
  function onBodyInput() {
    if (!editorRef.current) return;
    // Sanitize before storing — saved template body never contains scripts/event handlers
    setDraft(d => ({ ...d, body: sanitizeHtml(editorRef.current.innerHTML) }));
    setDirty(true);
  }

  function exec(cmd, value = null) {
    editorRef.current?.focus();
    document.execCommand(cmd, false, value);
    onBodyInput();
  }

  function insertToken(token) {
    editorRef.current?.focus();
    // Insert as plain text at cursor
    document.execCommand('insertText', false, token);
    onBodyInput();
  }

  function previewPrint() {
    printContract(draft, previewCtx, { title: draft.name || 'Preview', autoPrint: false });
  }

  // ── render ──────────────────────────────────────────────────────
  return (
    <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
      {/* Confirm delete overlay */}
      {confirmDelete && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: 'var(--sur)', borderRadius: 12, padding: '24px 28px', minWidth: 320, textAlign: 'center', boxShadow: '0 8px 40px rgba(0,0,0,0.25)' }}>
            <div style={{ fontSize: 28, marginBottom: 10 }}>⚠️</div>
            <div style={{ fontSize: 14, fontWeight: 600 }}>Delete "{confirmDelete.name}"?</div>
            <div style={{ fontSize: 11, color: 'var(--tx2)', marginBottom: 20 }}>This cannot be undone.</div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
              <button className="btn btn-ghost" onClick={() => setConfirmDelete(null)}>Cancel</button>
              <button className="btn btn-primary" style={{ background: '#ef4444' }} onClick={doDelete}>Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Sidebar: template list ─────────────────────────────── */}
      <div style={{ width: 240, flexShrink: 0, background: 'var(--sur)', border: '1px solid var(--bd)', borderRadius: 10, padding: 10, position: 'sticky', top: 0 }}>
        <button className="btn btn-primary btn-sm" style={{ width: '100%', marginBottom: 6 }} onClick={startNew}>+ New Template</button>
        <button
          className="btn btn-ghost btn-sm"
          style={{ width: '100%', marginBottom: 10, fontSize: 11, color: 'var(--tx3)' }}
          title="Replace all templates with the default seed (loses any custom edits)"
          onClick={() => {
            if (window.confirm('Reset all agreement templates to the default seed?\n\nThis will replace any custom edits you have made. Your rentals, customers, and other data are not affected.')) {
              resetAgreementTemplates && resetAgreementTemplates();
              showToast('✓ Templates reset to defaults');
              setCreating(false);
              setDirty(false);
            }
          }}
        >
          ↻ Reset to defaults
        </button>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {agreementTemplates.map(t => {
            const active = !creating && t.id === selectedId;
            const appliesLabel = APPLIES_TO.find(a => a.val === (t.appliesTo || 'both'))?.label || '';
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => { if (dirty && !window.confirm('Discard unsaved changes?')) return; setCreating(false); setSelectedId(t.id); }}
                style={{
                  display: 'flex', alignItems: 'flex-start', gap: 8, padding: '8px 10px',
                  background: active ? 'var(--ac-l)' : 'transparent',
                  border: '1px solid ' + (active ? 'var(--ac-b)' : 'transparent'),
                  borderRadius: 7, cursor: 'pointer', textAlign: 'left',
                }}
              >
                <span style={{ fontSize: 18 }}>{t.icon || '📄'}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--tx)', display: 'flex', alignItems: 'center', gap: 4 }}>
                    {t.name}
                    {t.isDefault && <span style={{ fontSize: 8, background: 'var(--gr)', color: '#fff', padding: '1px 4px', borderRadius: 4 }}>DEFAULT</span>}
                  </div>
                  <div style={{ fontSize: 10, color: 'var(--tx3)', marginTop: 1 }}>{appliesLabel}</div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Main editor ─────────────────────────────────────────── */}
      <div style={{ flex: 1, minWidth: 0 }}>
        {!selected ? (
          <div style={{ textAlign: 'center', padding: 48, color: 'var(--tx3)' }}>
            <div style={{ fontSize: 40, marginBottom: 10 }}>📋</div>
            <div style={{ fontSize: 13 }}>Select a template or create a new one</div>
          </div>
        ) : (
          <>
            {/* Header: name / icon / appliesTo / actions */}
            <div style={{ background: 'var(--sur)', border: '1px solid var(--bd)', borderRadius: 10, padding: 14, marginBottom: 12 }}>
              <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start', marginBottom: 12 }}>
                <div style={{ flexShrink: 0 }}>
                  <div style={{ fontSize: 9, fontWeight: 700, color: 'var(--tx3)', marginBottom: 5, textTransform: 'uppercase', letterSpacing: 0.6 }}>Icon</div>
                  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', maxWidth: 220 }}>
                    {TEMPLATE_ICONS.map(ic => (
                      <button key={ic} onClick={() => update({ icon: ic })}
                        style={{
                          fontSize: 16, padding: '4px 7px', borderRadius: 6, cursor: 'pointer', background: 'var(--sur)',
                          border: '2px solid ' + (draft.icon === ic ? 'var(--ac)' : 'var(--bd)'),
                        }}>{ic}</button>
                    ))}
                  </div>
                </div>
                <div style={{ flex: 1, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <div className="fg" style={{ margin: 0 }}>
                    <label className="fl">Template name</label>
                    <input className="fi" placeholder="e.g. Standard Vehicle Lease" value={draft.name} onChange={e => update({ name: e.target.value })} />
                  </div>
                  <div className="fg" style={{ margin: 0 }}>
                    <label className="fl">Description (internal)</label>
                    <input className="fi" placeholder="e.g. For all corporate leases" value={draft.desc} onChange={e => update({ desc: e.target.value })} />
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                <div style={{ display: 'flex', gap: 4 }}>
                  {APPLIES_TO.map(opt => (
                    <button key={opt.val} type="button"
                      onClick={() => update({ appliesTo: opt.val })}
                      className={draft.appliesTo === opt.val ? 'btn btn-primary btn-sm' : 'btn btn-ghost btn-sm'}
                      style={{ fontSize: 11 }} title={opt.desc}>
                      {opt.label}
                    </button>
                  ))}
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', userSelect: 'none', fontSize: 11, color: 'var(--tx2)', marginLeft: 8 }}>
                  <input type="checkbox" checked={!!draft.isDefault} onChange={e => update({ isDefault: e.target.checked })} style={{ accentColor: 'var(--ac)' }} />
                  Set as default
                </label>
                <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
                  <button className="btn btn-ghost btn-sm" onClick={previewPrint}>🖨️ Preview print</button>
                  {!creating && !draft.isDefault && (
                    <button className="btn btn-ghost btn-sm" style={{ color: '#ef4444' }} onClick={() => tryDelete(draft)}>Delete</button>
                  )}
                  {creating && <button className="btn btn-ghost btn-sm" onClick={cancelEdit}>Cancel</button>}
                  <button className="btn btn-primary btn-sm" onClick={save} disabled={!dirty && !creating}>
                    {creating ? 'Create' : (dirty ? 'Save changes' : 'Saved')}
                  </button>
                </div>
              </div>
            </div>

            {/* Editor + Tokens + Preview */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 240px', gap: 12, alignItems: 'flex-start' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {/* Body editor */}
                <div style={{ background: 'var(--sur)', border: '1px solid var(--bd)', borderRadius: 10, overflow: 'hidden' }}>
                  <div style={{ display: 'flex', gap: 4, padding: 8, borderBottom: '1px solid var(--bd)', background: 'var(--sur2)' }}>
                    {[
                      { cmd: 'bold',           label: 'B',  style: { fontWeight: 700 } },
                      { cmd: 'italic',         label: 'I',  style: { fontStyle: 'italic' } },
                      { cmd: 'underline',      label: 'U',  style: { textDecoration: 'underline' } },
                      { cmd: 'formatBlock',    arg: 'H1',   label: 'H1', style: { fontSize: 14, fontWeight: 700 } },
                      { cmd: 'formatBlock',    arg: 'H2',   label: 'H2', style: { fontSize: 13, fontWeight: 700 } },
                      { cmd: 'formatBlock',    arg: 'P',    label: '¶' },
                      { cmd: 'insertUnorderedList', label: '•' },
                      { cmd: 'insertOrderedList',   label: '1.' },
                      { cmd: 'removeFormat',   label: '⨯ Clear' },
                    ].map((t, i) => (
                      <button key={i} type="button" onMouseDown={e => e.preventDefault()}
                        onClick={() => exec(t.cmd, t.arg)}
                        style={{ padding: '4px 9px', fontSize: 11, border: '1px solid var(--bd)', background: 'var(--sur)', borderRadius: 5, cursor: 'pointer', ...(t.style || {}) }}>
                        {t.label}
                      </button>
                    ))}
                  </div>
                  <div
                    ref={editorRef}
                    contentEditable
                    suppressContentEditableWarning
                    onInput={onBodyInput}
                    onPaste={e => {
                      e.preventDefault();
                      const html = e.clipboardData.getData('text/html');
                      const text = e.clipboardData.getData('text/plain');
                      // Prefer sanitised HTML (keeps formatting), fall back to plain text
                      const safe = html ? sanitizeHtml(html) : (text || '').replace(/[&<>]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c]));
                      document.execCommand('insertHTML', false, safe);
                      onBodyInput();
                    }}
                    style={{
                      minHeight: 480, padding: '18px 24px', fontSize: 13, lineHeight: 1.65, color: '#222',
                      background: '#fff', outline: 'none', overflowY: 'auto',
                    }}
                    placeholder="Paste your contract here. Click a token chip on the right to insert {{variables}}."
                  />
                  <div style={{ padding: '6px 10px', borderTop: '1px solid var(--bd)', background: 'var(--sur2)', fontSize: 10, color: 'var(--tx3)' }}>
                    💡 Tip: paste your existing contract from Word — formatting (bold, lists, headings) is preserved. Click a token on the right to insert it at the cursor.
                  </div>
                </div>

                {/* Live preview */}
                <details style={{ background: 'var(--sur)', border: '1px solid var(--bd)', borderRadius: 10 }} open>
                  <summary style={{ padding: '10px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer', background: 'var(--sur2)', borderRadius: '10px 10px 0 0' }}>
                    👁️ Live preview (with sample data)
                  </summary>
                  <div style={{ padding: 14, background: 'var(--sur2)' }}>
                    <ContractDocument template={draft} context={previewCtx} />
                  </div>
                </details>
              </div>

              {/* Tokens panel */}
              <div style={{ background: 'var(--sur)', border: '1px solid var(--bd)', borderRadius: 10, padding: 10, position: 'sticky', top: 0, maxHeight: '85vh', overflowY: 'auto' }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--tx3)', textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 8 }}>Insert variable</div>
                {CONTRACT_TOKEN_GROUPS.map(grp => {
                  const open = openGroups[grp.id];
                  return (
                    <div key={grp.id} style={{ marginBottom: 6 }}>
                      <button type="button" onClick={() => setOpenGroups(g => ({ ...g, [grp.id]: !open }))}
                        style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '5px 7px', fontSize: 11, fontWeight: 600, background: 'var(--sur2)', border: '1px solid var(--bd)', borderRadius: 5, cursor: 'pointer' }}>
                        {grp.label}
                        <span style={{ fontSize: 10, color: 'var(--tx3)' }}>{open ? '▾' : '▸'}</span>
                      </button>
                      {open && (
                        <div style={{ marginTop: 4, display: 'flex', flexDirection: 'column', gap: 2 }}>
                          {grp.tokens.map(t => (
                            <button key={t.token} type="button"
                              onMouseDown={e => e.preventDefault()}
                              onClick={() => insertToken(t.token)}
                              title={t.token}
                              style={{ display: 'flex', justifyContent: 'space-between', gap: 6, padding: '4px 7px', fontSize: 10, background: 'transparent', border: 'none', borderRadius: 4, cursor: 'pointer', textAlign: 'left' }}
                              onMouseEnter={e => e.currentTarget.style.background = 'var(--ac-l)'}
                              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                              <span style={{ color: 'var(--tx2)' }}>{t.label}</span>
                              <code style={{ color: 'var(--ac)', fontSize: 9, fontFamily: "'DM Mono', monospace" }}>{t.token.replace(/[{}]/g, '')}</code>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
